/**
 * usePreset Hook
 * 
 * Manages preset state for a collection, auto-saves changes.
 * This follows the reference CMS implementation pattern.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { usePresetsStore } from '@/stores/presets';
import { useAuthStore } from '@/stores/auth';
import { Preset, LayoutQuery, LayoutOptions } from '@/types/presets';
import { cloneDeep, isEqual, debounce } from 'lodash';

interface UsePresetOptions {
    collection: string;
    bookmark?: number | null;
    temporary?: boolean;
}

interface UsePresetReturn {
    // State
    preset: Preset | null;
    bookmarkExists: boolean;
    bookmarkSaved: boolean;
    bookmarkIsMine: boolean;
    editable: boolean;
    busy: boolean;
    /** True once the preset for the current collection has been loaded from the store. */
    presetReady: boolean;

    // Layout
    layout: string;
    setLayout: (layout: string) => void;

    // Layout query (fields, sort, page, limit)
    layoutQuery: LayoutQuery | null;
    setLayoutQuery: (query: LayoutQuery | null) => void;

    // Layout options (widths, spacing, etc.)
    layoutOptions: LayoutOptions | null;
    allLayoutOptions: Record<string, LayoutOptions>;
    setLayoutOptions: (options: LayoutOptions | null) => void;

    // Filter
    filter: Record<string, unknown> | null;
    setFilter: (filter: Record<string, unknown> | null) => void;

    // Search
    search: string | null;
    setSearch: (search: string | null) => void;

    // Refresh interval
    refreshInterval: number | null;
    setRefreshInterval: (interval: number | null) => void;

    // Actions
    savePreset: (preset?: Partial<Preset>) => Promise<Preset | null>;
    saveCurrentAsBookmark: (overrides: Partial<Preset>) => Promise<Preset | null>;
    resetPreset: () => Promise<void>;
    clearLocalSave: () => void;
}

export function usePreset({
    collection,
    bookmark = null,
    temporary = false,
}: UsePresetOptions): UsePresetReturn {
    const { user } = useAuthStore();
    const {
        getPresetForCollection,
        getBookmark,
        savePreset: storeSavePreset,
        saveLocal,
        hydrated,
    } = usePresetsStore();

    const [busy, setBusy] = useState(false);
    const [bookmarkSaved, setBookmarkSaved] = useState(true);
    const [localPreset, setLocalPreset] = useState<Partial<Preset>>({ layout: 'tabular' });
    const [presetReady, setPresetReady] = useState(false);

    // Track if this is the first initialization and if we're currently creating
    const initialized = useRef(false);
    const isCreating = useRef(false);
    const autoSaveUnlocked = useRef(false);

    // Initialize local preset from store
    // If no preset exists for the collection, create one via API
    useEffect(() => {
        if (!hydrated || !collection || !user) return;

        // Prevent duplicate initialization
        if (initialized.current) return;

        const preset: Partial<Preset> = (() => {
            if (bookmark === null) {
                return { ...getPresetForCollection(collection) };
            }
            const bookmarkPreset = getBookmark(bookmark);
            return bookmarkPreset ? { ...bookmarkPreset } : { layout: 'tabular' };
        })();

        setLocalPreset(preset);
        setBookmarkSaved(true);
        initialized.current = true;
        setPresetReady(true);
    }, [collection, bookmark, hydrated, getPresetForCollection, getBookmark, user, temporary, storeSavePreset]);

    // Reset initialization when collection or bookmark changes
    useEffect(() => {
        initialized.current = false;
        isCreating.current = false;
        autoSaveUnlocked.current = false;
        setPresetReady(false);
    }, [collection, bookmark]);

    // Bookmark exists check
    const bookmarkExists = useMemo(() => {
        if (!bookmark) return false;
        return !!getBookmark(bookmark);
    }, [bookmark, getBookmark]);

    // Check if bookmark belongs to current user
    const bookmarkIsMine = useMemo(() => {
        return localPreset.user === user?.id;
    }, [localPreset.user, user?.id]);

    // Editable when scoped to current user (user preset or user bookmark). Role/global are locked.
    const editable = useMemo(() => {
        const scopedUser = localPreset.user ?? null;
        const scopedRole = localPreset.role ?? null;

        // Bookmarks: only user-owned are editable
        if (bookmarkExists) {
            return scopedUser === user?.id;
        }

        // Collection presets: editable only if user-specific and matches current user; role/global locked
        return scopedUser === user?.id && (!scopedRole || scopedRole === user?.role);
    }, [localPreset.user, localPreset.role, user?.id, user?.role, bookmarkExists]);

    // Current layout
    const layout = localPreset.layout || 'tabular';

    // Layout query for current layout
    const layoutQuery = useMemo(() => {
        return localPreset.layout_query?.[layout] || null;
    }, [localPreset.layout_query, layout]);

    // Layout options for current layout
    const layoutOptions = useMemo(() => {
        return localPreset.layout_options?.[layout] || null;
    }, [localPreset.layout_options, layout]);

    // Full layout_options structure (all layouts)
    const allLayoutOptions = useMemo(() => {
        return localPreset.layout_options || {};
    }, [localPreset.layout_options]);

    // Debounced auto-save function
    const autoSave = useMemo(
        () =>
            debounce(async (preset: Partial<Preset>) => {
                if (temporary) return;

                const result = await storeSavePreset(preset);

                // When a new preset is created, persist its id locally to avoid duplicate creations
                if (result?.id) {
                    setLocalPreset((prev: Partial<Preset>) => ({
                        ...prev,
                        ...preset,
                        id: result.id,
                        user: result.user ?? prev.user ?? user?.id ?? null,
                        role: result.role ?? prev.role ?? null,
                    }));
                }
            }, 450),
        [temporary, storeSavePreset, user?.id]
    );

    // Handle changes - either mark as unsaved (for bookmarks) or auto-save
    const handleChanges = useCallback(
        (newPreset: Partial<Preset>) => {
            if (!hydrated || !collection) return;

            // Don't persist if preset is locked (role/global)
            if (!editable) return;

            if (bookmarkExists && bookmark) {
                const bookmarkInStore = getBookmark(bookmark);
                setBookmarkSaved(isEqual(newPreset, bookmarkInStore));
                return;
            }

            // Skip the first change after hydration to avoid auto-creating presets on initial load
            if (!autoSaveUnlocked.current) {
                autoSaveUnlocked.current = true;
                return;
            }

            const current = getPresetForCollection(collection);
            if (current && isEqual(newPreset, current)) return;

            // Auto-save for non-bookmarks (creates on first user change after unlock)
            autoSave(cloneDeep(newPreset));
        },
        [bookmarkExists, bookmark, getBookmark, autoSave, getPresetForCollection, collection, hydrated, editable]
    );

    // Update preset helper
    const updatePreset = useCallback(
        (updates: Partial<Preset>, immediate = false) => {
            // Use Object.assign like the reference does (shallow merge)
            // This ensures arrays like 'fields' are replaced, not merged
            const newPreset = Object.assign({}, localPreset, updates);
            setLocalPreset(newPreset);

            if (immediate && !temporary && editable) {
                // Fire-and-forget save; capture the returned ID so subsequent saves
                // update the same record instead of creating duplicates.
                storeSavePreset(newPreset).then((result) => {
                    if (result?.id && !localPreset.id) {
                        setLocalPreset((prev) => ({
                            ...prev,
                            id: result.id,
                            user: result.user ?? prev.user ?? null,
                            role: result.role ?? prev.role ?? null,
                        }));
                        saveLocal(result as Preset);
                    }
                });
            } else {
                // handleChanges MUST run before saveLocal — it compares newPreset against
                // the current store value to decide whether to schedule an API save.
                // If saveLocal ran first, the store would already match newPreset and
                // handleChanges would skip the API call, leaving the server out of sync.
                handleChanges(newPreset);
            }

            // Optimistically sync to the Zustand store AFTER scheduling the API save,
            // so navigating away and back within the debounce window still sees the
            // latest state without re-reading stale data from the server.
            if (newPreset.id) {
                saveLocal(newPreset as Preset);
            }
        },
        [localPreset, temporary, storeSavePreset, handleChanges, editable, saveLocal]
    );

    // Setters
    const setLayout = useCallback(
        (newLayout: string) => {
            updatePreset({ layout: newLayout });
        },
        [updatePreset]
    );

    const setLayoutQuery = useCallback(
        (query: LayoutQuery | null) => {
            const { layout_query } = localPreset;
            if (query === null) {
                // Remove the layout key
                const newLayoutQuery = { ...layout_query };
                delete newLayoutQuery[layout];
                updatePreset({
                    layout_query: Object.keys(newLayoutQuery).length > 0 ? newLayoutQuery : null,
                });
            } else {
                updatePreset({
                    layout_query: {
                        ...layout_query,
                        [layout]: query,
                    },
                });
            }
        },
        [localPreset, layout, updatePreset]
    );

    const setLayoutOptions = useCallback(
        (options: LayoutOptions | null) => {
            const { layout_options } = localPreset;
            if (options === null) {
                // Remove the layout key
                const newLayoutOptions = { ...layout_options };
                delete newLayoutOptions[layout];
                updatePreset({
                    layout_options: Object.keys(newLayoutOptions).length > 0 ? newLayoutOptions : null,
                });
            } else {
                updatePreset({
                    layout_options: {
                        ...layout_options,
                        [layout]: options,
                    },
                });
            }
        },
        [localPreset, layout, updatePreset]
    );

    const setFilter = useCallback(
        (filter: Record<string, unknown> | null) => {
            // immediate=true: bypasses the autoSaveUnlocked guard, the isEqual check,
            // and the 450ms debounce — filter changes go straight to the API so they
            // always persist even if the user's first action is clearing filters.
            updatePreset({ filter }, true);
        },
        [updatePreset]
    );

    const setSearch = useCallback(
        (search: string | null) => {
            updatePreset({ search });
        },
        [updatePreset]
    );

    const setRefreshInterval = useCallback(
        (refresh_interval: number | null) => {
            updatePreset({ refresh_interval });
        },
        [updatePreset]
    );

    // Save preset to API
    const savePreset = useCallback(
        async (preset?: Partial<Preset>) => {
            if (temporary || !editable) return null;
            setBusy(true);

            try {
                const result = await storeSavePreset(preset || localPreset);

                if (result) {
                    setLocalPreset((prev: Partial<Preset>) => ({
                        ...prev,
                        id: result.id,
                        user: result.user,
                    }));
                    setBookmarkSaved(true);
                }

                return result;
            } finally {
                setBusy(false);
            }
        },
        [temporary, localPreset, storeSavePreset]
    );

    // Save current state as a new bookmark
    const saveCurrentAsBookmark = useCallback(
        async (overrides: Partial<Preset>) => {
            if (!user) return null;

            const data: Partial<Preset> = {
                ...localPreset,
                ...overrides,
                user: user.id,
            };

            // Remove ID to create new bookmark
            delete data.id;

            return await storeSavePreset(data);
        },
        [localPreset, user, storeSavePreset]
    );

    // Reset preset to defaults
    const resetPreset = useCallback(async () => {
        updatePreset(
            {
                layout_query: null,
                layout_options: null,
                layout: 'tabular',
                filter: null,
                search: null,
                refresh_interval: null,
            },
            true
        );
    }, [updatePreset]);

    // Revert local changes to saved bookmark
    const clearLocalSave = useCallback(() => {
        if (bookmark) {
            const bookmarkPreset = getBookmark(bookmark);
            if (bookmarkPreset) {
                setLocalPreset({ ...bookmarkPreset });
                setBookmarkSaved(true);
            }
        }
    }, [bookmark, getBookmark]);

    return {
        preset: localPreset as Preset,
        bookmarkExists,
        bookmarkSaved,
        bookmarkIsMine,
        editable,
        busy,
        presetReady,
        layout,
        setLayout,
        layoutQuery,
        setLayoutQuery,
        layoutOptions,
        allLayoutOptions,
        setLayoutOptions,
        filter: localPreset.filter || null,
        setFilter,
        search: localPreset.search || null,
        setSearch,
        refreshInterval: localPreset.refresh_interval || null,
        setRefreshInterval,
        savePreset,
        saveCurrentAsBookmark,
        resetPreset,
        clearLocalSave,
    };
}
